import { useParams, Link } from 'react-router-dom'
import {
  useContainers,
  useRestartContainer,
  useStopContainer,
  useStartContainer,
} from '@/hooks/useNodes'
import StatusBadge from '@/components/StatusBadge'
import LogViewer from '@/components/LogViewer'
import { formatTime } from '@/lib/utils'
import { Button, Card, EmptyState } from '@/admin-design/_common'

export default function ContainerDetail() {
  const { nodeId: nodeIdParam, containerId } = useParams<{
    nodeId: string
    containerId: string
  }>()
  const nodeId = Number(nodeIdParam)

  const { data: containers, isLoading } = useContainers(
    isFinite(nodeId) ? nodeId : undefined,
  )
  const container = containers?.find((c) => c.Id === containerId)

  const restart = useRestartContainer()
  const stop = useStopContainer()
  const start = useStartContainer()

  const isMutating =
    restart.isPending || stop.isPending || start.isPending

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-muted rounded animate-pulse" />
        <Card className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-5 bg-muted rounded w-2/3" />
          ))}
        </Card>
      </div>
    )
  }

  if (!container) {
    return (
      <div className="space-y-4">
        <Link to="/" className="text-sm text-info hover:underline">
          &larr; Back to Dashboard
        </Link>
        <Card>
          <EmptyState description="Container not found." />
        </Card>
      </div>
    )
  }

  const name =
    container.Names[0]?.replace(/^\//, '') ?? container.Id.slice(0, 12)
  const isRunning = container.State === 'running'
  const isStopped =
    container.State === 'exited' || container.State === 'created'

  const formatPorts = () => {
    if (!container.Ports || container.Ports.length === 0) return 'None'
    return container.Ports.map(
      (p) =>
        p.PublicPort
          ? `${p.IP || '0.0.0.0'}:${p.PublicPort} -> ${p.PrivatePort}/${p.Type}`
          : `${p.PrivatePort}/${p.Type}`,
    ).join(', ')
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <Link to="/" className="text-sm text-info hover:underline">
        &larr; Back to Dashboard
      </Link>

      {/* Container info */}
      <Card>
        <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-lg font-bold text-card-foreground break-all min-w-0">{name}</h1>
          <StatusBadge state={container.State} />
        </div>

        <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm break-all">
          <div>
            <span className="text-muted-foreground">Image</span>
            <p className="font-mono text-xs mt-0.5 break-all">
              {container.Image}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Status</span>
            <p className="mt-0.5">{container.Status}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Created</span>
            <p className="mt-0.5">{formatTime(container.Created)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Ports</span>
            <p className="font-mono text-xs mt-0.5">{formatPorts()}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Container ID</span>
            <p className="font-mono text-xs mt-0.5">{container.Id}</p>
          </div>
        </div>

        {/* Actions: Restart=info, Stop=destructive, Start=success */}
        <div className="px-4 py-3 border-t border-border bg-muted/50 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="info"
            size="sm"
            disabled={isMutating || !isRunning}
            onClick={() => restart.mutate({ id: container.Id, nodeId })}
          >
            Restart
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={isMutating || !isRunning}
            onClick={() => stop.mutate({ id: container.Id, nodeId })}
          >
            Stop
          </Button>
          <Button
            type="button"
            variant="success"
            size="sm"
            disabled={isMutating || !isStopped}
            onClick={() => start.mutate({ id: container.Id, nodeId })}
          >
            Start
          </Button>
          {isMutating && (
            <span className="text-xs text-muted-foreground animate-pulse ml-2">
              Processing...
            </span>
          )}
          {(restart.isError || stop.isError || start.isError) && (
            <span className="text-xs text-destructive ml-2">
              Action failed. Please try again.
            </span>
          )}
        </div>
      </Card>

      {/* Logs */}
      <LogViewer containerId={container.Id} nodeId={nodeId} />
    </div>
  )
}
